<template>
  <v-dialog v-model="showDialog" persistent max-width="400">
    <v-card>
      <v-toolbar color="primaryLight" dark dense>
        <v-toolbar-title>{{ editIncendio.LOCALIDAD }}</v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <v-form ref="form" v-model="isValid">
          <v-container fluid>
            <v-row>
              <v-col>
                <v-text-field v-model="editIncendio.NOMBRE" label="Nombre" placeholder="Nombre" prepend-icon="mdi-tag" hide-details />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-text-field v-model="editIncendio.MUNICIPIO" label="Municipio" placeholder="Municipio" prepend-icon="mdi-terrain" hide-details disabled />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-text-field v-model="editIncendio.LOCALIDAD" label="Localidad" placeholder="Localidad" prepend-icon="mdi-terrain" hide-details disabled />
              </v-col>
            </v-row>

            <v-row align="center">
              <v-col sm="6">
                <v-text-field v-model="editIncendio.LATITUD" label="Latitud" dense outlined prepend-icon="mdi-map-marker" :rules="latitudRules" @blur="updatedLatLon()" />
              </v-col>

              <v-col sm="5">
                <v-text-field v-model="editIncendio.LONGITUD" label="Longitud" dense outlined :rules="longitudRules" @blur="updatedLatLon()" />
              </v-col>

              <v-col sm="1">
                <v-btn icon color="blue" :disabled="saving" @click="editLocation">
                  <v-icon>mdi-map-marker-star</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <v-row align="center">
              <v-col sm="6">
                <v-text-field v-model="editIncendio.xETRS89" label="X" dense outlined prepend-icon="mdi-map-marker" :rules="utmRules" @blur="updatedXY()" />
              </v-col>

              <v-col sm="5">
                <v-text-field v-model="editIncendio.yETRS89" label="Y" dense outlined :rules="utmRules" @blur="updatedXY()" />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-select v-model="editIncendio.ID_TIPO_DETECCION" :items="tiposDeteccion" label="Detección" dense outlined prepend-icon="esri-icon-hollow-eye" item-text="DETECCION" item-value="ID_TIPO_DETECCION" placeholder="Detección" single-line autocomplete hide-details />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-textarea v-model="editIncendio.OBSERVACIONES" label="Observaciones" dense outlined placeholder="Observaciones" prepend-icon="esri-icon-comment" auto-grow hide-details :rules="[v => (v || '' ).length <= 100 || 'Número de caracteres maximo: 100']" />
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text color="red" :disabled="saving" @click="cancelar">Cancelar</v-btn>
        <v-btn text color="green darken-1" :disabled="!isValid || saving" :loading="saving" @click="aceptar">Aceptar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as ArcGIS from '../../../helpers/ArcGIS'
import { isFiniteNumber } from '@/helpers/Utils'

const COORD_DECIMALS = 5
const UTM_DECIMALS = 2

export default {
  props: {
    show: Boolean,
    incendio: Object,
    saving: Boolean
  },

  data: () => ({
    showDialog: false,
    isValid: false,
    editIncendio: {},
    isShowNewCoordenadas: false
  }),

  computed: {
    ...mapGetters('incendio', [
      'tiposDeteccion'
    ]),

    ...mapGetters('map', [
      'gettingNewCoordenadas',
      'newCoordenadas'
    ]),

    latitudRules() {
      return [
        v => isFiniteNumber(v) || 'Campo obligatorio y numérico',
        v => !isFiniteNumber(v) || (Number(v) >= -90 && Number(v) <= 90) || 'Latitud fuera de rango (-90 a 90)'
      ]
    },

    longitudRules() {
      return [
        v => isFiniteNumber(v) || 'Campo obligatorio y numérico',
        v => !isFiniteNumber(v) || (Number(v) >= -180 && Number(v) <= 180) || 'Longitud fuera de rango (-180 a 180)'
      ]
    },

    utmRules() {
      return [
        v => v === null || v === undefined || v === '' || isFiniteNumber(v) || 'Debe ser numérico'
      ]
    }
  },

  watch: {
    show() {
      this.showDialog = this.show

      if (this.show) {
        this.editIncendio = JSON.parse(JSON.stringify(this.incendio))
        const [x, y] = ArcGIS.convertirLatLonToETRS89(this.editIncendio.LATITUD, this.editIncendio.LONGITUD)
        this.editIncendio.xETRS89 = x.toFixed(UTM_DECIMALS)
        this.editIncendio.yETRS89 = y.toFixed(UTM_DECIMALS)
      } else {
        this.clearIncendio()
      }
    },

    gettingNewCoordenadas() {
      if (!this.gettingNewCoordenadas && this.isShowNewCoordenadas && this.show) { // Volver a mostrar el dialogo tras capturar el punto
        this.showDialog = true
      }
    },

    newCoordenadas() {
      if (this.isShowNewCoordenadas) {
        this.editIncendio.MUNICIPIO = this.newCoordenadas.MUNICIPIO || this.editIncendio.MUNICIPIO
        this.editIncendio.LOCALIDAD = this.newCoordenadas.LOCALIDAD || this.editIncendio.LOCALIDAD
        this.editIncendio.COD_PROV = this.newCoordenadas.COD_PROV ?? this.editIncendio.COD_PROV
        this.editIncendio.COD_CCAA = this.newCoordenadas.COD_CCAA ?? this.editIncendio.COD_CCAA

        this.editIncendio.LATITUD = this.newCoordenadas.LATITUD
        this.editIncendio.LONGITUD = this.newCoordenadas.LONGITUD

        this.editIncendio.xETRS89 = this.newCoordenadas.X.toFixed(UTM_DECIMALS)
        this.editIncendio.yETRS89 = this.newCoordenadas.Y.toFixed(UTM_DECIMALS)

        this.paintIncendio()
        this.showDialog = true
        this.isShowNewCoordenadas = false
      }
    }
  },

  methods: {
    ...mapActions('map', [
      'startNewCoordenadas'
    ]),

    clearIncendio() {
      ArcGIS.paintPositionIncendio(null)
    },

    paintIncendio() {
      ArcGIS.paintPositionIncendio({ LATITUD: this.editIncendio.LATITUD, LONGITUD: this.editIncendio.LONGITUD })
    },

    editLocation() {
      this.showDialog = false
      this.isShowNewCoordenadas = true
      this.startNewCoordenadas()
    },

    updatedLatLon() {
      const { LATITUD, LONGITUD } = this.editIncendio
      if (!isFiniteNumber(LATITUD) || !isFiniteNumber(LONGITUD)) return

      const [x, y] = ArcGIS.convertirLatLonToETRS89(LATITUD, LONGITUD)
      if (!Number.isFinite(x) || !Number.isFinite(y)) return

      this.editIncendio.xETRS89 = x.toFixed(UTM_DECIMALS)
      this.editIncendio.yETRS89 = y.toFixed(UTM_DECIMALS)
      this.paintIncendio()
    },

    updatedXY() {
      const { xETRS89, yETRS89 } = this.editIncendio
      if (!isFiniteNumber(xETRS89) || !isFiniteNumber(yETRS89)) return

      // convertirETRS89ToLatLon devuelve [lon, lat]: orden de proj4, longitud primero.
      const [lon, lat] = ArcGIS.convertirETRS89ToLatLon(xETRS89, yETRS89)
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return

      this.editIncendio.LONGITUD = lon.toFixed(COORD_DECIMALS)
      this.editIncendio.LATITUD = lat.toFixed(COORD_DECIMALS)
      this.paintIncendio()
    },

    cancelar() {
      this.$emit('cancelar')
    },

    aceptar() {
      // El backend hace Incendio.update(req.body) sin filtrar: solo pueden viajar
      // atributos del modelo INCENDIO.
      this.$emit('aceptar', {
        ID_INCENDIO: this.editIncendio.ID_INCENDIO,
        NOMBRE: this.editIncendio.NOMBRE,
        LATITUD: Number(this.editIncendio.LATITUD),
        LONGITUD: Number(this.editIncendio.LONGITUD),
        MUNICIPIO: this.editIncendio.MUNICIPIO,
        LOCALIDAD: this.editIncendio.LOCALIDAD,
        COD_PROV: this.editIncendio.COD_PROV,
        COD_CCAA: this.editIncendio.COD_CCAA,
        ID_TIPO_DETECCION: this.editIncendio.ID_TIPO_DETECCION,
        OBSERVACIONES: this.editIncendio.OBSERVACIONES
      })
    }
  }
}
</script>

<style scoped>
</style>
